import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, X } from 'lucide-react';
import Calendar from '../calendar/Calendar.js';
import { getFirstImageCandidate, setFirstImage, getDailyImages } from '../../api/iframeConfig.api';

// The signed S3 URL is virtual-hosted-style, so its pathname is the object key.
// This breaks if the backend switches to path-style URLs or fronts them with CloudFront.
const derivePathFromSignedUrl = (signedUrl) => {
  try {
    const { pathname } = new URL(signedUrl);
    return decodeURIComponent(pathname.replace(/^\/+/, ''));
  } catch (err) {
    console.error('No se pudo derivar el path desde la URL firmada:', err);
    return null;
  }
};

const parseDailyResponse = (data) => {
  const items = Array.isArray(data) ? data : [];
  return items
    .map((item) => {
      const url = typeof item === 'string' ? item : item.url;
      const date = typeof item === 'string' ? null : item.date;
      if (!url) return null;
      return { url, date, path: derivePathFromSignedUrl(url) };
    })
    .filter(Boolean);
};

const formatDateEs = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00.000Z`);
  const months = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];
  return `${date.getUTCDate()} de ${months[date.getUTCMonth()]} de ${date.getUTCFullYear()}`;
};

const IframeConfigTab = ({ user }) => {
  const feedId = user?.feedID;

  const [phase, setPhase] = useState('loading'); // loading | error | candidate | picker | confirmed
  const [errorMessage, setErrorMessage] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pickerDate, setPickerDate] = useState(null);
  const [dailyImages, setDailyImages] = useState([]);
  const [isDailyLoading, setIsDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(null);
  const [selectingPath, setSelectingPath] = useState(null);

  const loadCandidate = useCallback(async () => {
    if (!feedId) {
      setErrorMessage('Este proyecto no tiene un Feed ID configurado.');
      setPhase('error');
      return;
    }
    setPhase('loading');
    setErrorMessage(null);
    try {
      const data = await getFirstImageCandidate(feedId);
      setCandidate(data);
      setPhase(data.accepted ? 'confirmed' : 'candidate');
    } catch (error) {
      if (error.response?.status === 404) {
        setErrorMessage('No se encontraron imágenes en S3 para este proyecto.');
      } else {
        setErrorMessage('No se pudo cargar la imagen principal. Intentá de nuevo.');
      }
      setPhase('error');
    }
  }, [feedId]);

  useEffect(() => {
    loadCandidate();
  }, [loadCandidate]);

  const handleAccept = async () => {
    if (!candidate?.path) return;
    setIsSubmitting(true);
    try {
      const data = await setFirstImage(feedId, candidate.path);
      setCandidate(data);
      setPhase('confirmed');
    } catch (error) {
      setErrorMessage('No se pudo confirmar la imagen. Intentá de nuevo.');
      setPhase('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = () => {
    setPickerDate(candidate?.date ? new Date(`${candidate.date}T00:00:00.000Z`) : new Date());
    setDailyImages([]);
    setDailyError(null);
    setPhase('picker');
  };

  const fetchDailyImages = useCallback(async (date) => {
    setIsDailyLoading(true);
    setDailyError(null);
    try {
      const dateKey = date.toISOString().slice(0, 10);
      const data = await getDailyImages(feedId, dateKey);
      setDailyImages(parseDailyResponse(data));
    } catch (error) {
      setDailyError('No se pudieron cargar las imágenes de ese día.');
      setDailyImages([]);
    } finally {
      setIsDailyLoading(false);
    }
  }, [feedId]);

  const handlePickerDateSelect = (date) => {
    setPickerDate(date);
    fetchDailyImages(date);
  };

  const handleSelectImage = async (image) => {
    if (!image.path) {
      setDailyError('No se pudo determinar el path de esta imagen, probá con otra.');
      return;
    }
    setSelectingPath(image.path);
    try {
      const data = await setFirstImage(feedId, image.path);
      setCandidate(data);
      setPhase('confirmed');
    } catch (error) {
      setDailyError('No se pudo confirmar la imagen seleccionada. Intentá de nuevo.');
    } finally {
      setSelectingPath(null);
    }
  };

  if (phase === 'loading') {
    return <div className="p-8 text-center text-gray-600">Cargando imagen principal...</div>;
  }

  if (phase === 'error') {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-500 text-center">{errorMessage}</p>
      </div>
    );
  }

  if (phase === 'confirmed') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Imagen principal confirmada</span>
        </div>
        <div className="relative aspect-video max-w-md rounded-lg overflow-hidden shadow-sm">
          <img src={candidate.url} alt="Imagen principal" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <p className="text-sm text-gray-500">Fecha: {formatDateEs(candidate.date)}</p>
      </div>
    );
  }

  if (phase === 'candidate') {
    return (
      <div className="space-y-4">
        <p className="text-gray-600 text-sm">
          Esta es la imagen más antigua encontrada para tu proyecto. Confirmala o elegí otra.
        </p>
        <div className="relative aspect-video max-w-md rounded-lg overflow-hidden shadow-sm">
          <img src={candidate.url} alt="Candidata a imagen principal" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <p className="text-sm text-gray-500">Fecha: {formatDateEs(candidate.date)}</p>
        <div className="flex gap-3">
          <button
            onClick={handleAccept}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            {isSubmitting ? 'Confirmando...' : 'Aceptar'}
          </button>
          <button
            onClick={handleReject}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50"
          >
            <X className="w-4 h-4" />
            Rechazar y elegir otra
          </button>
        </div>
      </div>
    );
  }

  // phase === 'picker'
  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm">
        La primera foto encontrada fue del {formatDateEs(candidate?.date)}. Elegí un día del calendario para ver sus imágenes.
      </p>
      <Calendar user={user} selected={pickerDate} onDateSelect={handlePickerDateSelect} />

      {isDailyLoading && <div className="p-4 text-center text-gray-600">Cargando imágenes del día...</div>}

      {dailyError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {dailyError}
        </div>
      )}

      {!isDailyLoading && !dailyError && pickerDate && dailyImages.length === 0 && (
        <p className="text-gray-500 text-sm">No hay imágenes para ese día.</p>
      )}

      <div className="space-y-4">
        {dailyImages.map((image, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="relative w-full max-w-xl mx-auto aspect-video rounded-md overflow-hidden">
              <img src={image.url} alt={`Imagen ${index + 1}`} className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <button
              onClick={() => handleSelectImage(image)}
              disabled={selectingPath === image.path || !image.path}
              className="w-full max-w-xl mx-auto block px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition disabled:opacity-50 text-sm font-medium"
            >
              {selectingPath === image.path ? 'Confirmando...' : 'Seleccionar'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IframeConfigTab;
